import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';

export type AutoSyncInterval = 0 | 15 | 60 | 360 | 1440; // minutes; 0 = off

export const AUTO_SYNC_OPTIONS: { value: AutoSyncInterval; label: string }[] = [
  { value: 0, label: 'Off' },
  { value: 15, label: '15 min' },
  { value: 60, label: '1 hr' },
  { value: 360, label: '6 hr' },
  { value: 1440, label: '24 hr' },
];

const STORAGE_KEY = 'aurum_auto_sync_interval_v1';

interface Ctx {
  interval: AutoSyncInterval;
  setInterval: (m: AutoSyncInterval) => void;
  /** Epoch ms of the next scheduled tick (null if Off or no baseline yet). */
  nextRunAt: number | null;
  /** Register the refresh callback that the scheduler should invoke. */
  registerRunner: (run: () => Promise<unknown> | void) => void;
  /** Notify the scheduler that a sync (manual or auto) just completed at `ts`. */
  noteSyncedAt: (ts: number) => void;
}

const AutoSyncContext = createContext<Ctx | null>(null);

function readStored(): AutoSyncInterval {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw ? Number(raw) : 0;
    if ([0, 15, 60, 360, 1440].includes(n)) return n as AutoSyncInterval;
  } catch { /* noop */ }
  return 0;
}

export function AutoSyncProvider({ children }: { children: ReactNode }) {
  const [interval, setIntervalState] = useState<AutoSyncInterval>(() => readStored());
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const runnerRef = useRef<(() => Promise<unknown> | void) | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setInterval = useCallback((m: AutoSyncInterval) => {
    setIntervalState(m);
    try { localStorage.setItem(STORAGE_KEY, String(m)); } catch { /* noop */ }
  }, []);

  const registerRunner = useCallback((run: () => Promise<unknown> | void) => {
    runnerRef.current = run;
  }, []);

  const noteSyncedAt = useCallback((ts: number) => {
    setLastSyncedAt(ts);
  }, []);

  // Schedule the next tick whenever interval or last sync changes.
  // We use a single setTimeout (not setInterval) so each tick re-anchors
  // off the most recent successful sync time, regardless of trigger source.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (interval === 0) return;
    const baseline = lastSyncedAt ?? Date.now();
    const delay = Math.max(1000, baseline + interval * 60_000 - Date.now());
    timerRef.current = setTimeout(async () => {
      try {
        await runnerRef.current?.();
      } finally {
        // Even if runner didn't update lastSyncedAt (e.g. error path), advance
        // the baseline so we don't busy-loop on persistent failures.
        setLastSyncedAt(Date.now());
      }
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [interval, lastSyncedAt]);

  const nextRunAt = useMemo(() => {
    if (interval === 0) return null;
    const baseline = lastSyncedAt ?? Date.now();
    return baseline + interval * 60_000;
  }, [interval, lastSyncedAt]);

  const value = useMemo<Ctx>(() => ({
    interval,
    setInterval,
    nextRunAt,
    registerRunner,
    noteSyncedAt,
  }), [interval, setInterval, nextRunAt, registerRunner, noteSyncedAt]);

  return <AutoSyncContext.Provider value={value}>{children}</AutoSyncContext.Provider>;
}

export function useAutoSync() {
  const ctx = useContext(AutoSyncContext);
  if (!ctx) throw new Error('useAutoSync must be used inside AutoSyncProvider');
  return ctx;
}
